import _defineProperty from "/app/node_modules/.pnpm/next@13.4.17_@babel+core@7.14.3_@opentelemetry+api@1.4.1_react-dom@18.2.0_react@18.2.0_sass@1.49.9/node_modules/next/dist/compiled/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Card, Cell, Divider, DeprecatedIcon, Image, Rating, Spacer, Text } from '@vinted/web-ui';
import { compact, isEmpty, noop } from 'lodash';
import useLocation from 'hooks/useLocation';
import { FormattedDateMessage, FormattedMessage } from 'components/@support';
import { HideAtBreakpoint, ShowAtBreakpoint } from 'components/Breakpoint';
import DropdownMenu from 'components/DropdownMenu';
import FollowButton from 'components/FollowButton';
import { LinkifiedMessage } from 'components/LinkifiedMessage';
import SeparatedList from 'components/SeparatedList';
import TranslationControl from 'components/TranslationControl';
import UserBlockModal from 'components/UserBlockModal';
import UserTitle from 'components/UserTitle';
import useTranslate from 'hooks/useTranslate';
import { USER_STATUS_MEMBER_DELETED, VERIFICATION_LIST } from 'constants/index';
import { MEMBER_RESTORE_URL, MEMBER_TEMPLATES_URL, NEW_ADMIN_ALERT_URL, PROFILE_SETTINGS_URL } from 'constants/routes';
import { ClickableElement } from 'constants/tracking/clickable-elements';
import { Translation } from 'constants/translation';
import { AlertType } from 'constants/admin';
import { NEW_MESSAGE_URL } from 'constants/routes/inbox';
import { blockUser, getUserById, unblockUser } from 'data/api';
import { calculateRating, getEmptyUserImage } from 'data/utils/user';
import { isDateInTheFuture } from 'libs/utils/date';
import { navigateToPage, reloadPage } from 'libs/utils/window';
import { urlWithParams } from 'libs/utils/url';
import { getIsFeatureSwitchEnabled } from 'state/feature-switches/selectors';
import { getUser } from 'state/session/selectors';
import UserInfoBlock from '../InfoHeader/UserInfoBlock';
import FundraiserInfo from './FundraiserInfo';
import ProfileInfoBlock from './ProfileInfoBlock';
import FollowInfo from '../FollowInfo';
import UnauthenticatedReportModal from '../UnauthenticatedReportModal';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";

const renderWarningBlock = (key, text, enableHtml) => {
  const data = {
    text,
    enableHtml,
    iconName: 'block',
    theme: 'warning',
    color: DeprecatedIcon.Color.Warning
  };
  return /*#__PURE__*/_jsx(ProfileInfoBlock, {
    useFlexbox: true,
    data: data
  }, key);
};

const renderInfoBlock = (text, iconName, color) => {
  const data = {
    text,
    iconName,
    color
  };
  return /*#__PURE__*/_jsx(ProfileInfoBlock, {
    useFlexbox: true,
    data: data
  });
};

const ProfileInfo = _ref => {
  let {
    compactMode = false,
    onUserInfoClick,
    user,
    screen,
    viewingSelf = false,
    showActions = true,
    isShadowBanned = false,
    showSupportFunctionality,
    canSendTemplates,
    isContentTranslationEnabled,
    isAvailableForMigration,
    onTogglePoliciesModal = noop
  } = _ref;
  const {
    relativeUrl
  } = useLocation();
  const translate = useTranslate('user');
  const {
    0: isContentTranslated,
    1: setIsContentTranslated
  } = useState(false);
  const {
    0: translatedContent,
    1: setTranslatedContent
  } = useState(null);
  const {
    0: isBlockModalVisible,
    1: setIsBlockModalVisible
  } = useState(false);
  const {
    0: isReportModalVisible,
    1: setIsReportModalVisible
  } = useState(false);
  const isProfileReportingModalEnabled = useSelector(getIsFeatureSwitchEnabled('ntd_profile_reporting_modal'));
  const isProfileBlockingImprovementsEnabled = useSelector(getIsFeatureSwitchEnabled('profile_blocking_improvements'));
  const currentUser = useSelector(getUser);
  const {
    business: isBusinessAccount
  } = user;

  async function translateDescription() {
    if (translatedContent) {
      setIsContentTranslated(true);
      return;
    }

    const response = await getUserById({
      id: user.id,
      localize: true
    });
    if ('errors' in response) return;
    const {
      about
    } = response.user;
    if (!about) return;
    setTranslatedContent({
      about
    });
    setIsContentTranslated(true);
  }

  function showOriginal() {
    setIsContentTranslated(false);
  }

  function handleTranslate(isTranslated) {
    return isTranslated ? showOriginal() : translateDescription();
  }

  const toggleBlock = async () => {
    if (!currentUser) return;
    const toggleFunction = user.is_hated ? unblockUser : blockUser;
    await toggleFunction({
      currentUserId: currentUser.id,
      blockedUserId: user.id
    });
    reloadPage();
  };

  function handleBlockCancel() {
    setIsBlockModalVisible(false);
  }

  function handleModalBlockButtonClick() {
    setIsBlockModalVisible(false);
    reloadPage();
  }

  function handleBlockClick() {
    if (isProfileBlockingImprovementsEnabled) {
      setIsBlockModalVisible(true);
      return;
    }

    toggleBlock();
  }

  function handleReportClick() {
    if (currentUser || !isProfileReportingModalEnabled) {
      const url = urlWithParams(NEW_ADMIN_ALERT_URL, {
        ref_id: user.id,
        ref_type: AlertType.Member,
        ref_url: relativeUrl
      });
      navigateToPage(url);
      return;
    }

    setIsReportModalVisible(true);
  }

  function handleReportModalClose() {
    setIsReportModalVisible(false);
  }

  function renderProfileImage() {
    const photoUrl = user.photo ? user.photo.url : getEmptyUserImage();
    return /*#__PURE__*/_jsx(Image, {
      src: photoUrl,
      scaling: Image.Scaling.Fill
    });
  }

  function getRestrictionTranslationKey(key, isRestrictionPermanent) {
    const isDateVisibleForUser = viewingSelf || showSupportFunctionality || user.is_god;
    return isDateVisibleForUser && !isRestrictionPermanent ? key : "".concat(key, "_no_date");
  }

  function renderInfoHeader() {
    var _activeContent$about, _user$business_accoun;

    const {
      localization,
      id
    } = user;
    const activeContent = isContentTranslated && translatedContent ? translatedContent : user;
    const showTranslationControl = isContentTranslationEnabled && localization && localization !== Translation.None;
    const showUserActions = showSupportFunctionality || !viewingSelf;
    const toggleBlockText = user.is_hated ? translate('action.unblock') : translate('action.block');
    const actions = [{
      id: 'report',
      text: translate('action.report'),
      onClick: handleReportClick
    }, {
      id: user.is_hated ? 'unblock' : 'block',
      text: toggleBlockText,
      onClick: handleBlockClick
    }];
    const dropdownButtonProps = {
      styling: Button.Styling.Flat,
      deprecatedIcon: /*#__PURE__*/_jsx(DeprecatedIcon, {
        name: "horizontal-dots",
        color: DeprecatedIcon.Color.GreyscaleLevel4,
        testId: "actions-menu-button"
      }),
      size: Button.Size.Medium
    };
    const aboutText = (_activeContent$about = activeContent === null || activeContent === void 0 ? void 0 : activeContent.about) !== null && _activeContent$about !== void 0 ? _activeContent$about : '';
    return /*#__PURE__*/_jsx(ProfileInfoBlock, {
      children: /*#__PURE__*/_jsxs(Cell, {
        children: [/*#__PURE__*/_jsxs("div", {
          className: "u-flexbox u-justify-content-between",
          children: [/*#__PURE__*/_jsx(Text, {
            type: Text.Type.Heading,
            truncate: true,
            text: /*#__PURE__*/_jsx(UserTitle, {
              businessAccountName: (_user$business_accoun = user.business_account) === null || _user$business_accoun === void 0 ? void 0 : _user$business_accoun.name,
              login: user.login,
              isBusiness: user.business,
              isBadgeHidden: true,
              isLoginForced: true
            })
          }), showUserActions && /*#__PURE__*/_jsx("div", {
            children: /*#__PURE__*/_jsx(DropdownMenu, {
              actions: actions,
              buttonProps: dropdownButtonProps
            })
          })]
        }), aboutText && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Text, {
            format: true,
            children: /*#__PURE__*/_jsx(LinkifiedMessage, {
              text: aboutText
            })
          }), showTranslationControl && /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(Spacer, {}), /*#__PURE__*/_jsx(TranslationControl, {
              isTranslated: isContentTranslated,
              autotranslate: false,
              onClick: handleTranslate,
              tracking: {
                target: ClickableElement.TranslateDescription,
                targetDetails: id.toString()
              }
            })]
          })]
        })]
      })
    });
  }

  function renderBlockedWarningBlock() {
    return /*#__PURE__*/_jsx(ProfileInfoBlock, {
      children: /*#__PURE__*/_jsxs(Cell, {
        children: [/*#__PURE__*/_jsx(UserInfoBlock, {
          iconName: "block",
          iconColor: DeprecatedIcon.Color.Warning,
          children: /*#__PURE__*/_jsx(Text, {
            theme: "warning",
            children: translate('admin.messages.blocked')
          })
        }), isProfileBlockingImprovementsEnabled && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(Spacer, {
            size: Spacer.Size.Large
          }), /*#__PURE__*/_jsx(Button, {
            text: translate('action.unblock'),
            styling: Button.Styling.Filled,
            onClick: handleBlockClick
          })]
        })]
      })
    }, "blocked-warning");
  }

  function renderSellerPolicies() {
    if (!isBusinessAccount) return null;
    return /*#__PURE__*/_jsx(UserInfoBlock, {
      iconName: "document",
      children: /*#__PURE__*/_jsx("span", {
        onClick: onTogglePoliciesModal,
        children: /*#__PURE__*/_jsx(Text, {
          clickable: true,
          text: translate('seller_policies.title')
        })
      })
    });
  }

  function renderWarningParts() {
    const {
      is_hated,
      account_ban_date,
      forum_ban_date,
      account_status,
      id,
      is_forum_ban_permanent,
      is_account_ban_permanent
    } = user;
    const showDeleted = account_status === USER_STATUS_MEMBER_DELETED;
    const parts = compact([!is_hated && account_ban_date && renderWarningBlock('banned', translate(getRestrictionTranslationKey('admin.messages.banned', !!is_account_ban_permanent), {
      date: account_ban_date
    })), is_hated && renderBlockedWarningBlock(), !is_hated && forum_ban_date && renderWarningBlock('forum_banned', translate(getRestrictionTranslationKey('admin.messages.forum_banned', !!is_forum_ban_permanent), {
      date: forum_ban_date
    })), isShadowBanned && renderWarningBlock('shadow_banned_no_date', translate('admin.messages.shadow_banned_no_date')), showDeleted && renderWarningBlock('deleted', translate('admin.messages.deleted', {
      'member-restore-url': chunks => /*#__PURE__*/_jsx("a", {
        href: MEMBER_RESTORE_URL(id),
        children: chunks
      })
    }), true)]);
    if (isEmpty(parts)) return null;
    return parts;
  }

  function renderFeedback() {
    const {
      feedback_reputation,
      feedback_count
    } = user;
    const rating = calculateRating(feedback_reputation);
    return /*#__PURE__*/_jsx(ProfileInfoBlock, {
      children: /*#__PURE__*/_jsx(Cell, {
        type: Cell.Type.Navigating,
        onClick: onUserInfoClick,
        chevron: true,
        children: /*#__PURE__*/_jsx(Rating, {
          aria: {
            'aria-label': rating && translate('a11y.rating', {
              rating,
              max_rating: 5
            }) || ''
          },
          value: rating,
          text: translate('feedback.reviews', {
            count: feedback_count
          }, {
            count: feedback_count
          }),
          emptyStateText: translate('feedback.no_reviews')
        })
      })
    });
  }

  function renderDonation() {
    var _user$fundraiser;

    if (!((_user$fundraiser = user.fundraiser) !== null && _user$fundraiser !== void 0 && _user$fundraiser.active)) return null;
    return /*#__PURE__*/_jsx(ProfileInfoBlock, {
      children: /*#__PURE__*/_jsx(FundraiserInfo, {
        user: user,
        screen: screen
      })
    });
  }

  function renderMemberStatuses() {
    const {
      moderator,
      volunteer_moderator,
      is_identity,
      is_god
    } = user;
    const isMigrationIndicatorVisible = (showSupportFunctionality || is_god) && isAvailableForMigration;
    const parts = compact([moderator && renderInfoBlock(translate('status.product_team'), 'shield-outlined', DeprecatedIcon.Color.Expose), volunteer_moderator && renderInfoBlock(translate('status.volunteer_moderator'), 'shield-outlined', DeprecatedIcon.Color.Success), is_identity && renderInfoBlock(translate('status.identity_confirmed'), 'check-circle', DeprecatedIcon.Color.Success), isMigrationIndicatorVisible && renderInfoBlock(translate('status.available_for_migration'), 'check-circle-filled', DeprecatedIcon.Color.Success)]);
    if (isEmpty(parts)) return null;
    return parts;
  }

  function renderBusinessEmail() {
    var _user$business_accoun2, _user$business_accoun3;

    const hidePersonalInfo = isBusinessAccount && ((_user$business_accoun2 = user.business_account) === null || _user$business_accoun2 === void 0 ? void 0 : _user$business_accoun2.entity_type) === 'sole_trader';
    const businessEmail = isBusinessAccount && ((_user$business_accoun3 = user.business_account) === null || _user$business_accoun3 === void 0 ? void 0 : _user$business_accoun3.email);
    if (!businessEmail) return null;
    return /*#__PURE__*/_jsx(UserInfoBlock, {
      iconName: "envelope",
      children: hidePersonalInfo ? /*#__PURE__*/_jsx("span", {
        children: businessEmail
      }) : /*#__PURE__*/_jsx("a", {
        href: "mailto:".concat(businessEmail),
        children: businessEmail
      })
    });
  }

  function renderBusinessPhone() {
    var _user$business_accoun4, _user$business_accoun5;

    const hidePersonalInfo = isBusinessAccount && ((_user$business_accoun4 = user.business_account) === null || _user$business_accoun4 === void 0 ? void 0 : _user$business_accoun4.entity_type) === 'sole_trader';
    const businessPhone = isBusinessAccount && ((_user$business_accoun5 = user.business_account) === null || _user$business_accoun5 === void 0 ? void 0 : _user$business_accoun5.phone_number);
    if (!businessPhone) return null;
    return /*#__PURE__*/_jsx(UserInfoBlock, {
      iconName: "envelope",
      children: hidePersonalInfo ? /*#__PURE__*/_jsx("span", {
        children: businessPhone
      }) : /*#__PURE__*/_jsx("a", {
        href: "tel:".concat(businessPhone),
        children: businessPhone
      })
    });
  }

  function renderBusinessVerifications() {
    var _user$business_accoun6, _user$business_accoun7, _user$business_accoun8;

    if (!isBusinessAccount) return null;
    const businessLegalCode = (_user$business_accoun6 = user.business_account) === null || _user$business_accoun6 === void 0 ? void 0 : _user$business_accoun6.legal_code;
    const businessRegistrarName = (_user$business_accoun7 = user.business_account) === null || _user$business_accoun7 === void 0 ? void 0 : _user$business_accoun7.registrar_name;
    const selfCertified = (_user$business_accoun8 = user.business_account) === null || _user$business_accoun8 === void 0 ? void 0 : _user$business_accoun8.verified_identity;
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(UserInfoBlock, {
        iconName: "check-circle",
        children: translate('verification.business_identity')
      }), businessLegalCode && /*#__PURE__*/_jsxs(UserInfoBlock, {
        iconName: "check-circle",
        children: [translate('verification.legal_code'), " ", businessLegalCode]
      }), businessRegistrarName && /*#__PURE__*/_jsx(UserInfoBlock, {
        iconName: "check-circle",
        testId: "business-verification-registrar-name",
        children: businessRegistrarName
      }), selfCertified && /*#__PURE__*/_jsx(UserInfoBlock, {
        iconName: "check-circle",
        testId: "business-verification-self-certified",
        children: translate('verification.complies_with_eu_law')
      })]
    });
  }

  function renderVerifications() {
    const filteredVerifications = VERIFICATION_LIST.filter(item => {
      var _user$verification$it;

      return (_user$verification$it = user.verification[item]) === null || _user$verification$it === void 0 ? void 0 : _user$verification$it.valid;
    });
    if (isEmpty(filteredVerifications) && !isBusinessAccount) return null;

    if (!compactMode) {
      return /*#__PURE__*/_jsx(ProfileInfoBlock, {
        useFlexbox: true,
        children: /*#__PURE__*/_jsxs("div", {
          children: [/*#__PURE__*/_jsx(Text, {
            type: Text.Type.Caption,
            text: /*#__PURE__*/_jsx(FormattedMessage, {
              id: "user.verification.title"
            })
          }), filteredVerifications.map(verification => /*#__PURE__*/_jsx(UserInfoBlock, {
            iconName: "check-circle",
            children: translate("verification.".concat(verification))
          }, verification)), renderBusinessVerifications()]
        })
      });
    }

    return /*#__PURE__*/_jsx(UserInfoBlock, {
      iconName: "check-circle",
      children: filteredVerifications.map(item => translate("verification.".concat(item))).concat(isBusinessAccount ? [translate('verification.business_identity')] : []).join(', ')
    });
  }

  const renderLocationContent = () => {
    const {
      expose_location,
      city,
      country_title_local,
      business_account
    } = user;
    const hidePersonalInfo = isBusinessAccount && (business_account === null || business_account === void 0 ? void 0 : business_account.entity_type) === 'sole_trader';
    const businessAddress = isBusinessAccount && (business_account === null || business_account === void 0 ? void 0 : business_account.address);
    const locationDescription = expose_location && city ? [city, country_title_local].join(', ') : country_title_local;

    if (locationDescription && !businessAddress || hidePersonalInfo) {
      return locationDescription;
    }

    if (businessAddress && !hidePersonalInfo) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [(businessAddress === null || businessAddress === void 0 ? void 0 : businessAddress.line1) && /*#__PURE__*/_jsx("div", {
          children: businessAddress.line1
        }), (businessAddress === null || businessAddress === void 0 ? void 0 : businessAddress.line2) && /*#__PURE__*/_jsx("div", {
          children: businessAddress.line2
        }), (businessAddress === null || businessAddress === void 0 ? void 0 : businessAddress.postal_code) && (businessAddress === null || businessAddress === void 0 ? void 0 : businessAddress.city) && /*#__PURE__*/_jsxs("div", {
          children: [businessAddress.postal_code, " ", businessAddress.city]
        }), (businessAddress === null || businessAddress === void 0 ? void 0 : businessAddress.country_title) && /*#__PURE__*/_jsx("div", {
          children: businessAddress.country_title
        })]
      });
    }

    return null;
  };

  const renderLocationBlock = () => {
    if (!renderLocationContent()) {
      return null;
    }

    return /*#__PURE__*/_jsx(UserInfoBlock, {
      iconName: "location-pin",
      testId: "profile-location-info",
      children: renderLocationContent()
    });
  };

  function renderFollowMessage() {
    const {
      followers_count,
      following_count,
      id
    } = user;
    return /*#__PURE__*/_jsx(FollowInfo, {
      followersCount: followers_count,
      followingCount: following_count,
      userId: id,
      viewingSelf: viewingSelf
    });
  }

  function renderAdditionalInfo() {
    const {
      email,
      last_loged_on_ts,
      created_at,
      msg_template_count
    } = user;
    const memberSinceDate = new Date(created_at);
    return /*#__PURE__*/_jsx(ProfileInfoBlock, {
      useFlexbox: true,
      children: /*#__PURE__*/_jsxs("div", {
        children: [renderLocationBlock(), renderBusinessEmail(), renderBusinessPhone(), last_loged_on_ts && /*#__PURE__*/_jsx(UserInfoBlock, {
          iconName: "clock",
          children: /*#__PURE__*/_jsx(Text, {
            children: /*#__PURE__*/_jsx(FormattedDateMessage, {
              id: "user.last_login",
              date: new Date(last_loged_on_ts),
              relativeDate: true
            })
          })
        }), /*#__PURE__*/_jsx(UserInfoBlock, {
          iconName: "rss",
          children: renderFollowMessage()
        }), showSupportFunctionality && /*#__PURE__*/_jsx(UserInfoBlock, {
          iconName: "member",
          children: /*#__PURE__*/_jsx(Text, {
            children: /*#__PURE__*/_jsx(FormattedDateMessage, {
              id: "user.since",
              date: memberSinceDate,
              relativeDate: false
            })
          })
        }), showSupportFunctionality && /*#__PURE__*/_jsx(UserInfoBlock, {
          iconName: "envelope",
          children: /*#__PURE__*/_jsx(Text, {
            text: email
          })
        }), canSendTemplates && /*#__PURE__*/_jsx(UserInfoBlock, {
          iconName: "flag",
          children: /*#__PURE__*/_jsx("a", {
            href: MEMBER_TEMPLATES_URL(user.id),
            children: translate('admin.template_count', {
              count: msg_template_count
            }, {
              count: msg_template_count
            })
          })
        }), renderSellerPolicies()]
      })
    });
  }

  function renderTopActions() {
    if (!viewingSelf) return null;
    return /*#__PURE__*/_jsx(ProfileInfoBlock, {
      useFlexbox: true,
      children: /*#__PURE__*/_jsx(Button, {
        deprecatedIcon: /*#__PURE__*/_jsx(DeprecatedIcon, {
          name: "pencil",
          color: DeprecatedIcon.Color.GreyscaleLevel3
        }),
        url: PROFILE_SETTINGS_URL,
        text: translate('action.edit_profile'),
        theme: "muted",
        size: Button.Size.Medium
      })
    });
  }

  function renderBottomActions() {
    const showMessageButton = showActions && !viewingSelf && user.allow_direct_messaging;
    const showFollowButton = showActions && !viewingSelf && !isDateInTheFuture(user.closet_countdown_end_date);
    const isUserBanned = user.account_ban_date;
    const isActionAvailable = showMessageButton || showFollowButton;
    if (!isActionAvailable || isUserBanned) return null;
    return /*#__PURE__*/_jsxs(ProfileInfoBlock, {
      useFlexbox: true,
      children: [showMessageButton && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Button, {
          text: translate('action.write_message'),
          url: urlWithParams(NEW_MESSAGE_URL, {
            receiver_id: user.id,
            ref_url: relativeUrl
          }),
          size: Button.Size.Medium,
          testId: "profile-info-message-button"
        }), /*#__PURE__*/_jsx("div", {
          children: /*#__PURE__*/_jsx(Spacer, {
            orientation: Spacer.Orientation.Vertical,
            size: Spacer.Size.Large
          })
        })]
      }), showFollowButton && /*#__PURE__*/_jsx(FollowButton, {
        userId: user.id,
        isActive: user.is_favourite,
        size: Button.Size.Medium,
        testId: "profile-info-follow-button"
      })]
    });
  }

  function renderCompactUserInfo() {
    var _user$business_accoun9;

    const {
      feedback_reputation,
      photo,
      feedback_count
    } = user;
    const rating = calculateRating(feedback_reputation);
    const photoUrl = photo ? photo.url : getEmptyUserImage();
    const infoProps = {
      prefix: /*#__PURE__*/_jsx(Image, {
        src: photoUrl,
        styling: Image.Styling.Circle,
        size: Image.Size.Large
      }),
      title: /*#__PURE__*/_jsx(UserTitle, {
        businessAccountName: (_user$business_accoun9 = user.business_account) === null || _user$business_accoun9 === void 0 ? void 0 : _user$business_accoun9.name,
        login: user.login,
        isBusiness: user.business
      }),
      body: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(Rating, {
          aria: {
            'aria-label': rating && translate('a11y.rating', {
              rating,
              max_rating: 5
            }) || ''
          },
          value: rating,
          text: feedback_count,
          emptyStateText: translate('feedback.no_reviews')
        }), /*#__PURE__*/_jsx(Spacer, {
          size: Spacer.Size.Small
        })]
      }),
      type: Cell.Type.Navigating,
      onClick: onUserInfoClick,
      chevron: true,
      id: 'profile-compact-info'
    };
    return /*#__PURE__*/_jsx(Cell, _objectSpread({}, infoProps));
  }

  function renderCompactAdditionalInfo() {
    return /*#__PURE__*/_jsxs(Cell, {
      children: [renderVerifications(), renderLocationBlock(), renderBusinessEmail(), /*#__PURE__*/_jsx(UserInfoBlock, {
        iconName: "rss",
        children: renderFollowMessage()
      })]
    });
  }

  function renderMainInfo() {
    return /*#__PURE__*/_jsxs(SeparatedList, {
      separator: /*#__PURE__*/_jsx(Divider, {}),
      children: [renderTopActions(), renderInfoHeader(), renderWarningParts(), renderDonation(), renderFeedback(), renderMemberStatuses(), renderVerifications(), renderAdditionalInfo(), renderBottomActions()]
    });
  }

  function renderUserBlockModal() {
    return /*#__PURE__*/_jsx(UserBlockModal, {
      username: user.login,
      isUserBlocked: user.is_hated,
      userId: user.id,
      isVisible: isBlockModalVisible,
      onCancel: handleBlockCancel,
      onConfirm: handleModalBlockButtonClick
    });
  }

  if (compactMode) {
    return /*#__PURE__*/_jsxs(SeparatedList, {
      separator: /*#__PURE__*/_jsx(Divider, {}),
      children: [renderCompactUserInfo(), renderDonation(), renderCompactAdditionalInfo(), renderBottomActions()]
    });
  }

  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(HideAtBreakpoint, {
      breakpoint: "tablets",
      children: /*#__PURE__*/_jsx(Card, {
        children: /*#__PURE__*/_jsxs("div", {
          className: "u-overflow-hidden",
          children: [renderProfileImage(), renderMainInfo()]
        })
      })
    }), /*#__PURE__*/_jsxs(ShowAtBreakpoint, {
      breakpoint: "tablets",
      children: [/*#__PURE__*/_jsx(Spacer, {
        size: Spacer.Size.Large
      }), /*#__PURE__*/_jsxs("div", {
        className: "u-flexbox",
        children: [/*#__PURE__*/_jsx(Spacer, {
          orientation: Spacer.Orientation.Vertical,
          size: Spacer.Size.Large
        }), /*#__PURE__*/_jsx(Card, {
          children: /*#__PURE__*/_jsx("div", {
            className: "u-overflow-hidden",
            children: renderProfileImage()
          })
        }), /*#__PURE__*/_jsx(Spacer, {
          orientation: Spacer.Orientation.Vertical,
          size: Spacer.Size.Large
        }), /*#__PURE__*/_jsx("div", {
          className: "u-flex-grow",
          children: /*#__PURE__*/_jsx(Card, {
            children: /*#__PURE__*/_jsx("div", {
              className: "u-overflow-hidden",
              children: renderMainInfo()
            })
          })
        }), /*#__PURE__*/_jsx(Spacer, {
          orientation: Spacer.Orientation.Vertical,
          size: Spacer.Size.Large
        })]
      })]
    }), /*#__PURE__*/_jsx(UnauthenticatedReportModal, {
      isVisible: isReportModalVisible,
      onClose: handleReportModalClose
    }), renderUserBlockModal()]
  });
};

export default ProfileInfo;